import type { GameEvent, GiftInfo, HelloPayload, SourceStatus } from '@rekrut/shared';
import type { IEventSource } from './types.js';

export interface SocketSourceOptions {
  url: string;
  /** Комната стримера: один сервер обслуживает много эфиров одновременно. */
  streamerId: string;
  reconnect?: { baseMs: number; maxAttempts: number; jitter: number };
}

/**
 * Сетевой источник: подписка на комнату стримера у Connector Service.
 *
 * Реализован на нативном WebSocket, чтобы игра не тянула зависимость раньше,
 * чем появится сам коннектор. Когда коннектор поедет на Socket.IO (§3.2),
 * меняется ровно этот файл — контракт GameEvent и IEventSource остаются.
 *
 * Реконнект с экспоненциальной задержкой и джиттером ±30% (§4.6): при падении
 * сервиса подписи все оверлеи не должны долбиться в сервер синхронно.
 */
export class SocketEventSource implements IEventSource {
  readonly name = 'socket';
  status: SourceStatus = 'idle';
  giftCatalog: GiftInfo[] = [];

  private ws: WebSocket | null = null;
  private evCb: ((e: GameEvent) => void) | null = null;
  private stCb: ((s: SourceStatus) => void) | null = null;
  private attempt = 0;
  private timer: number | null = null;
  private closedByUs = false;

  constructor(private readonly opts: SocketSourceOptions) {}

  async connect(streamerId = this.opts.streamerId): Promise<void> {
    this.closedByUs = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const url = new URL(this.opts.url);
    url.searchParams.set('streamerId', streamerId);
    const ws = new WebSocket(url.toString());
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.setStatus('live');
    };

    ws.onmessage = (msg) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(msg.data));
      } catch {
        return;
      }
      const env = parsed as { t?: string; d?: unknown };
      if (env.t === 'hello') {
        const hello = env.d as HelloPayload;
        this.giftCatalog = hello.giftCatalog ?? [];
        return;
      }
      if (env.t === 'event' && env.d) this.evCb?.(env.d as GameEvent);
    };

    ws.onerror = () => {
      /* onclose всё равно придёт следом */
    };

    ws.onclose = () => {
      this.ws = null;
      if (this.closedByUs) return;
      this.scheduleReconnect(streamerId);
    };
  }

  private scheduleReconnect(streamerId: string): void {
    const r = this.opts.reconnect ?? { baseMs: 2000, maxAttempts: 5, jitter: 0.3 };
    this.attempt++;

    // Попытки кончились — уходим в режим ожидания эфира. Игра при этом
    // продолжает идти на уже присутствующих шарах (§4.6).
    const capped = this.attempt > r.maxAttempts;
    const delay = capped
      ? 60000
      : r.baseMs * 2 ** (this.attempt - 1) * (1 + (Math.random() * 2 - 1) * r.jitter);

    this.setStatus(capped ? 'offline' : 'reconnecting');
    this.timer = window.setTimeout(() => void this.connect(streamerId), delay);
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
    this.setStatus('offline');
  }

  onEvent(cb: (e: GameEvent) => void): void {
    this.evCb = cb;
  }

  onStatus(cb: (s: SourceStatus) => void): void {
    this.stCb = cb;
  }

  private setStatus(s: SourceStatus): void {
    this.status = s;
    this.stCb?.(s);
  }
}
